import { useShallow } from 'zustand/react/shallow';
import { useChatStore } from '@/store';

// The sidebar/drawer layouts only need chat *metadata* (title, flags,
// timestamps, ownership) — never `messages`. Subscribing to `state.chats`
// directly re-renders the entire app shell on every streaming token because
// the active chat object (and the chats array) get new references on each
// flush. This hook projects each chat down to a messages-free meta object and
// keeps the previous object reference whenever no metadata actually changed,
// so `useShallow` sees a stable array during streaming and the sidebar only
// re-renders on real changes (new chat, rename, pin, delete, etc.).

// chat object ref -> projected meta (fast path: store kept the chat ref stable)
const metaByRef = new WeakMap();
// chat id -> last projected meta (value compare when the chat ref changed)
const metaById = new Map();

function shallowEqualObjects(a, b) {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

function projectChatMeta(chat) {
  const byRef = metaByRef.get(chat);
  if (byRef) return byRef;

  const { messages, ...meta } = chat;
  const prev = metaById.get(chat.id);
  const result = prev && shallowEqualObjects(prev, meta) ? prev : meta;

  metaById.set(chat.id, result);
  metaByRef.set(chat, result);
  return result;
}

/**
 * Metadata-only view of the chat list for sidebars/drawers. Same shape as
 * `state.chats` minus `messages`, with stable references across streaming
 * updates.
 */
export function useSidebarChats() {
  return useChatStore(useShallow(state => state.chats.map(projectChatMeta)));
}

export default useSidebarChats;
